/**
 * Page Mes Commandes (FrontOffice)
 * Affiche les commandes de l'utilisateur avec statut de livraison
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, orderApi, Order, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, OrderStatus } from '@e-store/shared';
import { AmazonHeader } from '../components/AmazonHeader';
import './MyOrdersPage.css';

export function MyOrdersPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/orders');
      return;
    }
    loadOrders();
  }, [isAuthenticated, navigate, selectedStatus]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await orderApi.getMyOrders({
        status: selectedStatus || undefined,
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      setOrders(response.data?.orders || []);
    } catch (err: any) {
      setError('Erreur lors du chargement des commandes');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'pending_confirmation':
        return '⏳';
      case 'confirmed':
        return '✓';
      case 'preparing':
        return '📦';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      default:
        return '•';
    }
  };

  const getProgressPercentage = (status: OrderStatus): number => {
    switch (status) {
      case 'pending_confirmation':
        return 20;
      case 'confirmed':
        return 40;
      case 'preparing':
        return 60;
      case 'shipped':
        return 80;
      case 'delivered':
        return 100;
      case 'cancelled':
        return 0;
      default:
        return 0;
    }
  };

  const toggleOrderDetails = (orderId: string) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Filtrer les commandes par recherche
  const filteredOrders = orders.filter((order) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Recherche par numéro de commande
    if (order.orderNumber.toLowerCase().includes(query)) return true;

    // Recherche par nom d'article
    if (order.lines.some(line =>
      line.articleName.toLowerCase().includes(query) ||
      line.articleReference.toLowerCase().includes(query)
    )) return true;

    return false;
  });

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="my-orders-page">
      <AmazonHeader showSearch={true} />

      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Mes Commandes</h1>
          <p className="page-description">
            Suivez vos commandes et leur statut de livraison
          </p>
        </div>

        {/* Recherche */}
        <div className="orders-search">
          <div className="search-input-wrapper">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher par numéro de commande ou article..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Filtres */}
        <div className="orders-filters">
          <button
            className={`filter-btn ${selectedStatus === '' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('')}
          >
            Toutes
          </button>
          <button
            className={`filter-btn ${selectedStatus === 'pending_confirmation' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('pending_confirmation')}
          >
            En attente
          </button>
          <button
            className={`filter-btn ${selectedStatus === 'confirmed' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('confirmed')}
          >
            Confirmées
          </button>
          <button
            className={`filter-btn ${selectedStatus === 'preparing' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('preparing')}
          >
            En préparation
          </button>
          <button
            className={`filter-btn ${selectedStatus === 'shipped' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('shipped')}
          >
            En livraison
          </button>
          <button
            className={`filter-btn ${selectedStatus === 'delivered' ? 'active' : ''}`}
            onClick={() => setSelectedStatus('delivered')}
          >
            Livrées
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="message error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Liste des commandes */}
        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Chargement de vos commandes...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h2>Aucune commande</h2>
            <p>Vous n'avez pas encore passé de commande</p>
            <Link to="/catalog" className="btn btn-primary">
              Découvrir le catalogue
            </Link>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h2>Aucun résultat</h2>
            <p>Aucune commande ne correspond à "{searchQuery}"</p>
            <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
              Effacer la recherche
            </button>
          </div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map((order) => (
              <div key={order.orderId} className="order-card">
                {/* En-tête de la commande */}
                <div className="order-header">
                  <div className="order-info">
                    <div className="order-number">
                      <span className="label">Commande N°</span>
                      <span className="value">{order.orderNumber}</span>
                    </div>
                    <div className="order-date">
                      <span className="label">Passée le</span>
                      <span className="value">{formatDate(order.createdAt)}</span>
                    </div>
                    <div className="order-total">
                      <span className="label">Total</span>
                      <span className="value">{formatPrice(order.totalTTC)}</span>
                    </div>
                  </div>
                  <div className="order-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: ORDER_STATUS_COLORS[order.status] }}
                    >
                      {getStatusIcon(order.status)} {ORDER_STATUS_LABELS[order.status]}
                    </span>
                  </div>
                </div>

                {/* Barre de progression */}
                {order.status !== 'cancelled' && (
                  <div className="order-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${getProgressPercentage(order.status)}%`,
                          backgroundColor: ORDER_STATUS_COLORS[order.status],
                        }}
                      ></div>
                    </div>
                    <div className="progress-steps">
                      <div className={`step ${getProgressPercentage(order.status) >= 20 ? 'completed' : ''}`}>
                        <span className="step-icon">📝</span>
                        <span className="step-label">Commande passée</span>
                      </div>
                      <div className={`step ${getProgressPercentage(order.status) >= 40 ? 'completed' : ''}`}>
                        <span className="step-icon">✓</span>
                        <span className="step-label">Confirmée</span>
                      </div>
                      <div className={`step ${getProgressPercentage(order.status) >= 60 ? 'completed' : ''}`}>
                        <span className="step-icon">📦</span>
                        <span className="step-label">Préparation</span>
                      </div>
                      <div className={`step ${getProgressPercentage(order.status) >= 80 ? 'completed' : ''}`}>
                        <span className="step-icon">🚚</span>
                        <span className="step-label">En livraison</span>
                      </div>
                      <div className={`step ${getProgressPercentage(order.status) >= 100 ? 'completed' : ''}`}>
                        <span className="step-icon">✅</span>
                        <span className="step-label">Livrée</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Commande annulée */}
                {order.status === 'cancelled' && (
                  <div className="order-cancelled">
                    <span className="cancelled-icon">❌</span>
                    <div className="cancelled-info">
                      <strong>Commande annulée</strong>
                      {order.cancelReason && <p>{order.cancelReason}</p>}
                      {order.cancelledAt && (
                        <span className="cancelled-date">
                          Annulée le {formatDate(order.cancelledAt)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Bouton détails */}
                <button
                  className="btn-details"
                  onClick={() => toggleOrderDetails(order.orderId)}
                >
                  {expandedOrder === order.orderId ? 'Masquer les détails' : 'Voir les détails'}
                  <svg
                    className={`chevron ${expandedOrder === order.orderId ? 'open' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {/* Détails de la commande */}
                {expandedOrder === order.orderId && (
                  <div className="order-details">
                    {/* Articles */}
                    <div className="details-section">
                      <h3>Articles commandés</h3>
                      <div className="order-items">
                        {order.lines.map((line) => (
                          <div key={line.lineId} className="order-item">
                            <div className="item-image">
                              {line.articleImage ? (
                                <img src={line.articleImage} alt={line.articleName} />
                              ) : (
                                <div className="no-image">📦</div>
                              )}
                            </div>
                            <div className="item-info">
                              <span className="item-name">{line.articleName}</span>
                              <span className="item-ref">Réf: {line.articleReference}</span>
                            </div>
                            <div className="item-qty">
                              <span>Qté: {line.quantity}</span>
                            </div>
                            <div className="item-price">
                              <span>{formatPrice(line.totalTTC)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Retrait sur place (aucune adresse attendue) */}
                    {order.deliveryMode === 'pickup' && (
                      <div className="details-section">
                        <h3>Mode de réception</h3>
                        <p><strong>🏬 Retrait sur place</strong></p>
                        <p>
                          Vous venez chercher votre commande. Nous vous contactons dès qu'elle est
                          prête.
                        </p>
                      </div>
                    )}

                    {/* Livraison */}
                    {order.delivery && (
                      <div className="details-section">
                        <h3>Informations de livraison</h3>

                        {/* Adresse */}
                        <div className="delivery-address">
                          <h4>Adresse de livraison</h4>
                          {order.delivery.recipientName && (
                            <p><strong>{order.delivery.recipientName}</strong></p>
                          )}
                          <p>{order.delivery.address}</p>
                          <p>
                            {order.delivery.postalCode} {order.delivery.city}
                          </p>
                          {order.delivery.country && <p>{order.delivery.country}</p>}
                          {order.delivery.phone && (
                            <p className="tracking-info">
                              <span className="tracking-icon">📞</span>
                              <strong>Tél. de contact:</strong>{' '}
                              <a href={`tel:${order.delivery.phone}`} className="contact-phone">
                                {order.delivery.phone}
                              </a>
                            </p>
                          )}
                        </div>

                        {/* Date estimée et instructions */}
                        {(order.delivery.estimatedDate || order.delivery.estimatedDeliveryDate || order.delivery.instructions) && (
                          <div className="delivery-info-box" style={{ marginTop: '15px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                            {(order.delivery.estimatedDate || order.delivery.estimatedDeliveryDate) && (
                              <p className="estimated-date" style={{ marginBottom: '8px' }}>
                                <span style={{ fontSize: '1.1em' }}>📅</span> <strong>Livraison estimée:</strong> {formatDate(order.delivery.estimatedDate || order.delivery.estimatedDeliveryDate)}
                              </p>
                            )}
                            {order.delivery.instructions && (
                              <p className="delivery-instructions" style={{ marginTop: '8px' }}>
                                <span style={{ fontSize: '1.1em' }}>📝</span> <strong>Instructions:</strong> {order.delivery.instructions}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Frais de livraison */}
                        {order.delivery.deliveryFeeType && (
                          <div className="delivery-fee-info" style={{ marginTop: '15px', padding: '12px', backgroundColor: order.delivery.deliveryFeeType === 'free' ? '#f0fdf4' : '#fef9e7', borderRadius: '8px', border: `1px solid ${order.delivery.deliveryFeeType === 'free' ? '#bbf7d0' : '#fde68a'}` }}>
                            <p style={{ fontWeight: '500', marginBottom: '5px' }}>
                              <span style={{ fontSize: '1.1em' }}>💰</span> <strong>Frais de livraison:</strong>
                            </p>
                            {order.delivery.deliveryFeeType === 'included' && (
                              <p style={{ color: '#059669' }}>
                                ✓ Inclus dans le total ({formatPrice(order.delivery.deliveryFee || 0)})
                              </p>
                            )}
                            {order.delivery.deliveryFeeType === 'cash_on_delivery' && (
                              <p style={{ color: '#d97706' }}>
                                💵 Payable à la livraison: {formatPrice(order.delivery.deliveryFeeCOD || 0)}
                              </p>
                            )}
                            {order.delivery.deliveryFeeType === 'free' && (
                              <p style={{ color: '#059669' }}>
                                🎉 Livraison gratuite !
                              </p>
                            )}
                          </div>
                        )}

                        {/* Informations de suivi */}
                        {(order.delivery.trackingNumber || order.delivery.carrier || order.delivery.deliveryCompany || order.delivery.driverPhone) && (
                          <div className="delivery-tracking" style={{ marginTop: '15px' }}>
                            <h4>Suivi de la livraison</h4>
                            {order.delivery.trackingNumber && (
                              <p className="tracking-info">
                                <span className="tracking-icon">📍</span>
                                <strong>N° de suivi:</strong> {order.delivery.trackingNumber}
                              </p>
                            )}
                            {order.delivery.carrier && (
                              <p className="tracking-info">
                                <span className="tracking-icon">📦</span>
                                <strong>Transporteur:</strong> {order.delivery.carrier}
                              </p>
                            )}
                            {order.delivery.deliveryCompany && (
                              <p className="tracking-info">
                                <span className="tracking-icon">🏢</span>
                                <strong>Société de livraison:</strong> {order.delivery.deliveryCompany}
                              </p>
                            )}
                            {order.delivery.driverPhone && (
                              <p className="tracking-info">
                                <span className="tracking-icon">📞</span>
                                <strong>Tél. du chauffeur:</strong>{' '}
                                <a href={`tel:${order.delivery.driverPhone}`} className="driver-phone">
                                  {order.delivery.driverPhone}
                                </a>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Récapitulatif */}
                    <div className="details-section">
                      <h3>Récapitulatif</h3>
                      <div className="order-summary">
                        <div className="summary-line">
                          <span>Sous-total HT</span>
                          <span>{formatPrice(order.totalHT)}</span>
                        </div>
                        <div className="summary-line">
                          <span>TVA</span>
                          <span>{formatPrice(order.totalTVA)}</span>
                        </div>
                        {order.delivery?.deliveryFee && order.delivery.deliveryFee > 0 && (
                          <div className="summary-line">
                            <span>Frais de livraison</span>
                            <span>{formatPrice(order.delivery.deliveryFee)}</span>
                          </div>
                        )}
                        <div className="summary-line total">
                          <span>Total TTC</span>
                          <span>{formatPrice(order.totalTTC)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {order.notes && (
                      <div className="details-section">
                        <h3>Notes</h3>
                        <p className="order-notes">{order.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
